import { Dialog, DialogBody, DialogContent } from '@civilgrid/ui/components';
import { type HTMLAttributes, useEffect, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { formatGeoItemDialogTitle, geoItemPropValueText } from '~/common/miscUtils';
import type { GeoItem } from '~/common/types';
import { GeoItemMarkerDot } from '~/components2/Map/GeoItemMarkerDot';
import { POTHOLE_FILL, POTHOLE_STROKE } from '../map/siteDataLegend.helpers';
import { GeoItemFilePreviewProvider } from './common';
import { ManholeDialogContent, PotholeDialogContent } from './GeoItemDialogContent';
import { closePothole, openPothole, type PotholeSurface, potholeOpenItemStore, releaseSurface } from './openItemStore';

type PotholeMapLayersProps = {
  /** Already filtered to the org's report type (`potholeLog` or `manholeInspection`). */
  geoItems: readonly GeoItem[];
  /** Org report type — drives marker title + which edit dialog opens. */
  mobileReportType?: string;
  /** Ids highlighted from the side-list selection (tab mode); empty in layer mode. */
  selectedRowIds?: ReadonlySet<string>;
  /** Which surface this is, so the record it opens is shown by its own dialog host only. */
  surface: PotholeSurface;
  /** False on read-only surfaces (print view): draw the dots, nothing to click or hover. */
  interactive?: boolean;
};

/**
 * Renders pothole / manhole geo-items as a GeoJSON source of clickable markers, each
 * opening the full edit dialog. Shared by the Potholing tab (with side-list selection)
 * and the project-scoped "Site Data" map layer (map/PotholesLayer.tsx, no list — click →
 * modal only). Extracted from potholing/index.tsx so both paths render identically and the
 * geo-item shape isn't hand-duplicated.
 */
export function PotholeMapLayers({
  geoItems,
  mobileReportType,
  selectedRowIds,
  surface,
  interactive = true,
}: PotholeMapLayersProps) {
  const [hoveredItem, setHoveredItem] = useState<GeoItem>();
  const { openItemId } = useSnapshot(potholeOpenItemStore);
  const isManhole = mobileReportType === 'manholeInspection';

  return (
    <MapGL.Source
      id="potholing"
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features: geoItems.map((item) => ({
          type: 'Feature',
          geometry: item.geometry,
          properties: {},
        })),
      }}
    >
      {geoItems.map((item) => {
        const lengthFormat = 'in.';
        const [lng, lat] = item.geometry.coordinates;
        const depthToTop = item.fields['Depth to Top'];
        const depthToBottom = item.fields['Depth to Bottom'];
        const itemNumber = geoItemPropValueText(item.fields['PH#']?.value || item.fields['MH#']?.value);
        const title = isManhole
          ? formatGeoItemDialogTitle('Manhole', itemNumber, 'Inspection')
          : formatGeoItemDialogTitle('Pothole', itemNumber, 'Log');

        // A read-only surface draws the dot alone — no button semantics, nothing to open, no hover card.
        const controls: HTMLAttributes<HTMLDivElement> = interactive
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': title,
              'aria-haspopup': 'dialog',
              'aria-expanded': openItemId === item.id,
              onClick: () => {
                openPothole(item.id as string, surface);
              },
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPothole(item.id as string, surface);
                }
              },
              onMouseEnter: () => {
                setHoveredItem(item);
              },
              onMouseLeave: () => {
                setHoveredItem(undefined);
              },
              onMouseDown: () => {
                setHoveredItem(undefined);
              },
            }
          : {};

        return (
          <div key={`geo-item-marker-${item.id}`} data-geo-item-marker={item.id} {...controls}>
            <MapGL.Marker
              longitude={lng}
              latitude={lat}
              anchor="center"
              onClick={() => {}}
              style={{
                fontSize: '1rem',
                zIndex: 10,
                cursor: interactive ? 'pointer' : 'default',
              }}
            >
              <GeoItemMarkerDot
                testId={`pothole-marker-${item.id}`}
                label={itemNumber}
                // Default fill/stroke shared with the legend swatch (siteDataLegend.helpers.ts).
                fill={item.fields?.Priority?.value === 'High' ? 'red' : POTHOLE_FILL}
                stroke={selectedRowIds?.has(item.id as string) ? 'blue' : POTHOLE_STROKE}
              />
              {hoveredItem?.id === item.id && (
                <MapGL.Popup
                  longitude={lng}
                  latitude={lat}
                  anchor="bottom"
                  offset={[0, -30]}
                  className="z-20"
                  closeButton={false}
                >
                  <div className="text-sm">
                    <div>
                      <span className="font-bold">{isManhole ? 'MH#' : 'PH#'}: </span>
                      {itemNumber}
                    </div>
                    <div>
                      <span className="font-bold">Date: </span>
                      {item.createdAt.toLocaleString()}
                    </div>
                    {depthToTop && (
                      <div>
                        <span className="font-bold">Depth to Top: </span>
                        {depthToTop?.value}&nbsp;{lengthFormat}
                      </div>
                    )}
                    {depthToBottom && (
                      <div>
                        <span className="font-bold">Depth to Bottom: </span>
                        {depthToBottom?.value}&nbsp;{lengthFormat}
                      </div>
                    )}
                  </div>
                </MapGL.Popup>
              )}
            </MapGL.Marker>
          </div>
        );
      })}
    </MapGL.Source>
  );
}

/**
 * The open record's edit dialog, rendered once per surface instead of once per marker. A dialog
 * owned by its marker can only ever be opened by clicking that marker, which leaves no way in for a
 * `?geoItem=` link. Mount alongside {@link PotholeMapLayers} on each surface that renders markers.
 */
export function PotholeDialogHost({
  geoItems,
  mobileReportType,
  surface,
}: {
  geoItems: readonly GeoItem[];
  mobileReportType?: string;
  surface: PotholeSurface;
}) {
  const { openItemId, surface: openedOn } = useSnapshot(potholeOpenItemStore);
  const isManhole = mobileReportType === 'manholeInspection';
  // Only a record this surface opened: the other surface's selection must not spring open here.
  const item = openItemId && openedOn === surface ? geoItems.find((geoItem) => geoItem.id === openItemId) : undefined;

  // Radix never reports a close for a dialog that unmounts open, so drop the selection ourselves.
  useEffect(() => () => releaseSurface(surface), [surface]);

  // Also covers a record filtered out of this surface, so a stale id cannot open an empty dialog.
  if (!item) return null;

  const itemNumber = geoItemPropValueText(item.fields['PH#']?.value || item.fields['MH#']?.value);
  const title = isManhole
    ? formatGeoItemDialogTitle('Manhole', itemNumber, 'Inspection')
    : formatGeoItemDialogTitle('Pothole', itemNumber, 'Log');

  return (
    <GeoItemFilePreviewProvider>
      <Dialog
        open
        onOpenChange={(isOpen) => {
          if (!isOpen) closePothole();
        }}
      >
        <DialogContent
          title={title}
          className="z-[110] w-auto min-w-min max-w-[75vw]"
          overlayClassName="z-[100]"
          data-testid="pothole-record-dialog"
          // With no trigger Radix has nothing to return focus to, so send it back to the marker.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            document.querySelector<HTMLElement>(`[data-geo-item-marker="${item.id}"]`)?.focus();
          }}
        >
          <DialogBody>
            {/* Keyed: both forms seed react-hook-form from the record on mount only, so switching
                records without a remount would show the previous record's values. */}
            {isManhole ? (
              <ManholeDialogContent key={item.id} geoItem={item} closeDialog={closePothole} />
            ) : (
              <PotholeDialogContent key={item.id} geoItem={item} closeDialog={closePothole} />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </GeoItemFilePreviewProvider>
  );
}
